import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PortalLayout from './PortalLayout';
import { 
  Gift, 
  Users, 
  DollarSign, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Trash2,
  Plus,
  Minus,
  Link,
  Search,
  Calendar,
  Filter,
  X
} from 'lucide-react';

const AdminReferrals = ({ embedded = false }) => {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referralData, setReferralData] = useState({
    total_credits_outstanding: 0,
    total_referrals: 0,
    users_with_credit: [],
    referral_activity: []
  });
  
  // Filter and search states
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  
  // Form states
  const [revokeForm, setRevokeForm] = useState({ referrer_email: '', referred_email: '', reason: 'Job canceled before attempts' });
  const [adjustForm, setAdjustForm] = useState({ user_email: '', amount: 25, reason: '' });
  const [linkForm, setLinkForm] = useState({ portal_email: '', servemanager_email: '' });

  // Filtered referral activity
  const filteredActivity = useMemo(() => {
    return referralData.referral_activity.filter(activity => {
      // Status filter
      if (statusFilter !== 'all' && activity.status !== statusFilter) {
        return false;
      }
      
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          activity.referrer_email?.toLowerCase().includes(search) ||
          activity.referrer_company?.toLowerCase().includes(search) ||
          activity.referred_email?.toLowerCase().includes(search) ||
          activity.referred_company?.toLowerCase().includes(search) ||
          activity.invoice_id?.toLowerCase().includes(search)
        );
      }
      
      return true;
    });
  }, [referralData.referral_activity, statusFilter, searchTerm]);

  // Filtered users with credit
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return referralData.users_with_credit;
    
    const search = searchTerm.toLowerCase();
    return referralData.users_with_credit.filter(user =>
      user.email?.toLowerCase().includes(search) ||
      user.company_name?.toLowerCase().includes(search) ||
      user.referral_code?.toLowerCase().includes(search)
    );
  }, [referralData.users_with_credit, searchTerm]);

  const fetchReferrals = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch('/api/admin/referrals');
      if (!response.ok) {
        throw new Error('Failed to fetch referral data');
      }
      const data = await response.json();
      setReferralData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, []);

  const handleRevoke = async () => {
    setActionLoading(true);
    setActionMessage(null);
    try {
      const response = await authFetch(
        `/api/admin/referrals/revoke?referrer_email=${encodeURIComponent(revokeForm.referrer_email)}&referred_email=${encodeURIComponent(revokeForm.referred_email)}&reason=${encodeURIComponent(revokeForm.reason)}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (response.ok) {
        setActionMessage({ type: 'success', text: data.message });
        setShowRevokeModal(false);
        setRevokeForm({ referrer_email: '', referred_email: '', reason: 'Job canceled before attempts' });
        fetchReferrals();
      } else {
        setActionMessage({ type: 'error', text: data.detail || 'Failed to revoke' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdjust = async () => {
    setActionLoading(true);
    setActionMessage(null);
    try {
      const response = await authFetch(
        `/api/admin/referrals/adjust?user_email=${encodeURIComponent(adjustForm.user_email)}&amount=${adjustForm.amount}&reason=${encodeURIComponent(adjustForm.reason)}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (response.ok) {
        setActionMessage({ type: 'success', text: data.message });
        setShowAdjustModal(false);
        setAdjustForm({ user_email: '', amount: 25, reason: '' });
        fetchReferrals();
      } else {
        setActionMessage({ type: 'error', text: data.detail || 'Failed to adjust' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleLink = async () => {
    setActionLoading(true);
    setActionMessage(null);
    try {
      const response = await authFetch(
        `/api/admin/referrals/link-client?portal_email=${encodeURIComponent(linkForm.portal_email)}&servemanager_email=${encodeURIComponent(linkForm.servemanager_email)}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (response.ok) {
        setActionMessage({ type: 'success', text: data.message });
        setShowLinkModal(false);
        setLinkForm({ portal_email: '', servemanager_email: '' });
      } else {
        setActionMessage({ type: 'error', text: data.detail || 'Failed to link' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'earned':
        return <span className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">Earned</span>;
      case 'applied':
        return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">Applied</span>;
      case 'revoked':
        return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">Revoked</span>;
      case 'manual':
        return <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">Manual</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded-full">{status}</span>;
    }
  };

  const Wrapper = embedded ? React.Fragment : PortalLayout;

  return (
    <Wrapper>
      <div className="space-y-6" data-testid="admin-referrals">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Referral Management</h1>
            <p className="text-sm text-slate-500 mt-1">Track and manage client referral credits</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchReferrals}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Action Message */}
        {actionMessage && (
          <div className={`p-4 rounded-lg flex items-center gap-3 ${
            actionMessage.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {actionMessage.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span>{actionMessage.text}</span>
            <button onClick={() => setActionMessage(null)} className="ml-auto">
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-emerald-50 rounded-lg">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Credits Outstanding</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">
              ${referralData.total_credits_outstanding.toFixed(2)}
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Referrals</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">{referralData.total_referrals}</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Gift className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Users with Credit</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">{referralData.users_with_credit.length}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowRevokeModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all"
            data-testid="revoke-referral-btn"
          >
            <Trash2 className="w-4 h-4" />
            Revoke Referral
          </button>
          <button
            onClick={() => setShowAdjustModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-all"
            data-testid="adjust-credit-btn"
          >
            <Plus className="w-4 h-4" />
            Adjust Credit
          </button>
          <button
            onClick={() => setShowLinkModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-all"
            data-testid="link-email-btn"
          >
            <Link className="w-4 h-4" />
            Link Email
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by email, company, or invoice..."
                className="w-full pl-10 pr-10 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                data-testid="referral-search-input"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="clear-search-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Status Filter Buttons */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                {[
                  { value: 'all', label: 'All' },
                  { value: 'earned', label: 'Earned' },
                  { value: 'applied', label: 'Applied' },
                  { value: 'revoked', label: 'Revoked' }
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => setStatusFilter(filter.value)}
                    className={`px-3 py-1.5 text-sm font-medium transition-all ${
                      statusFilter === filter.value
                        ? 'bg-slate-900 text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`filter-${filter.value}-btn`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Active Filters Display */}
          {(searchTerm || statusFilter !== 'all') && (
            <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
              <span>Showing:</span>
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">
                  "{searchTerm}"
                  <button onClick={() => setSearchTerm('')} className="hover:text-blue-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {statusFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full capitalize">
                  {statusFilter}
                  <button onClick={() => setStatusFilter('all')} className="hover:text-slate-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              <span className="text-slate-400">
                ({filteredActivity.length} result{filteredActivity.length !== 1 ? 's' : ''})
              </span>
            </div>
          )}
        </div>

        {/* Users with Credit */}
        {filteredUsers.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="users-with-credit-table">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Users with Credit Balance</h2>
              <span className="text-sm text-slate-500">{filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Client</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Referral Code</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Total Earned</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Applied</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Current Balance</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Referrals</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredUsers.map((user, index) => (
                    <tr key={index} className="hover:bg-slate-50">
                      <td className="px-5 py-4">
                        <div>
                          <p className="font-medium text-slate-900">{user.company_name}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{user.referral_code}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="font-semibold text-slate-700">${(user.total_earned || 0).toFixed(2)}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="font-semibold text-blue-600">${(user.total_applied || 0).toFixed(2)}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="font-semibold text-emerald-600">${user.referral_credit.toFixed(2)}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-slate-600">{user.total_referrals}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Referral Activity */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="referral-activity-table">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Referral Activity</h2>
            <span className="text-sm text-slate-500">{filteredActivity.length} record{filteredActivity.length !== 1 ? 's' : ''}</span>
          </div>
          {filteredActivity.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Referrer</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Referred Client</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Amount</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Invoice Applied</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date Earned</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date Applied</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredActivity.map((activity, index) => (
                    <tr key={index} className="hover:bg-slate-50">
                      <td className="px-5 py-4">
                        <div>
                          <p className="font-medium text-slate-900">{activity.referrer_company}</p>
                          <p className="text-sm text-slate-500">{activity.referrer_email}</p>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div>
                          <p className="font-medium text-slate-900">{activity.referred_company}</p>
                          <p className="text-sm text-slate-500">{activity.referred_email}</p>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`font-semibold ${activity.credit_amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {activity.credit_amount >= 0 ? '+' : ''}${Math.abs(activity.credit_amount).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        {getStatusBadge(activity.status)}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-500">
                        {activity.invoice_id ? (
                          <span className="font-mono bg-slate-100 px-2 py-1 rounded">#{activity.invoice_id}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-500">
                        {formatDate(activity.earned_at)}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-500">
                        {activity.applied_at ? formatDate(activity.applied_at) : <span className="text-slate-400">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-5 py-12 text-center">
              <Gift className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No referral activity yet</p>
              <p className="text-sm text-slate-400 mt-1">Referrals will appear here when clients use referral codes</p>
            </div>
          )}
        </div>

        {/* Revoke Modal */}
        {showRevokeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Revoke Referral Credit</h3>
              <p className="text-sm text-slate-500 mb-4">
                Use this when a referred client cancels their job before any attempts were made.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Referrer Email</label>
                  <input
                    type="email"
                    value={revokeForm.referrer_email}
                    onChange={(e) => setRevokeForm({...revokeForm, referrer_email: e.target.value})}
                    placeholder="client@lawfirm.com"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Referred Client Email</label>
                  <input
                    type="email"
                    value={revokeForm.referred_email}
                    onChange={(e) => setRevokeForm({...revokeForm, referred_email: e.target.value})}
                    placeholder="newclient@firm.com"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
                  <input
                    type="text"
                    value={revokeForm.reason}
                    onChange={(e) => setRevokeForm({...revokeForm, reason: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowRevokeModal(false)}
                  className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRevoke}
                  disabled={actionLoading || !revokeForm.referrer_email || !revokeForm.referred_email}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Revoking...' : 'Revoke $25 Credit'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Adjust Modal */}
        {showAdjustModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Adjust Credit Balance</h3>
              <p className="text-sm text-slate-500 mb-4">
                Manually add or subtract credit from a client's account.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Client Email</label>
                  <input
                    type="email"
                    value={adjustForm.user_email}
                    onChange={(e) => setAdjustForm({...adjustForm, user_email: e.target.value})}
                    placeholder="client@lawfirm.com"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount (use negative to subtract)</label>
                  <input
                    type="number"
                    value={adjustForm.amount}
                    onChange={(e) => setAdjustForm({...adjustForm, amount: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
                  <input
                    type="text"
                    value={adjustForm.reason}
                    onChange={(e) => setAdjustForm({...adjustForm, reason: e.target.value})}
                    placeholder="Manual adjustment reason"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowAdjustModal(false)}
                  className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdjust}
                  disabled={actionLoading || !adjustForm.user_email || !adjustForm.reason}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Adjusting...' : `Adjust ${adjustForm.amount >= 0 ? '+' : ''}$${adjustForm.amount}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Link Email Modal */}
        {showLinkModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Link Client Email</h3>
              <p className="text-sm text-slate-500 mb-4">
                Link a ServeManager email to a portal account when they don't match.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Portal Email</label>
                  <input
                    type="email"
                    value={linkForm.portal_email}
                    onChange={(e) => setLinkForm({...linkForm, portal_email: e.target.value})}
                    placeholder="client@lawfirm.com"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">ServeManager Email</label>
                  <input
                    type="email"
                    value={linkForm.servemanager_email}
                    onChange={(e) => setLinkForm({...linkForm, servemanager_email: e.target.value})}
                    placeholder="billing@lawfirm.com"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowLinkModal(false)}
                  className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLink}
                  disabled={actionLoading || !linkForm.portal_email || !linkForm.servemanager_email}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Linking...' : 'Link Emails'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default AdminReferrals;
